'use client';
import { FC } from 'react';
import { Box, IconButton, SxProps, Theme, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { CompareItem } from './CompareBar';
import { useResponsive } from '../../../contexts/ResponsiveContext';
import { ProductsContent } from '../../../graphql/pimcore/modules/siteContent/types/ProductsContent';

interface ProductBoxInsideCompareBarProps {
    sx?: SxProps<Theme>;
    compareItem?: CompareItem;
    onClearCompareItem?: (basePn: string) => void;
    contentTree: ProductsContent;
}

const ProductBoxInsideCompareBar: FC<ProductBoxInsideCompareBarProps> = ({
    sx,
    compareItem,
    onClearCompareItem,
    contentTree,
}) => {
    const {
        isMaxScreen,
        isScreenXxl,
        isScreenXl,
        isScreenLg,
        isScreenMd,
        isScreenSm,
        isScreenXs,
        responsiveWidth,
    } = useResponsive();

    const dynamicButtonWidth = (() => {
        if (isMaxScreen || isScreenXxl || isScreenXl) return '250px';
        else return '200px';
    })();

    // 取得部件編號
    const getPartNumber = (): string => {
        if (!compareItem) return '';
        else if ('displayPn' in compareItem && compareItem.displayPn) {
            return compareItem.displayPn;
        } else if (
            'basePn' in compareItem &&
            typeof compareItem.basePn === 'string' &&
            compareItem.basePn
        ) {
            return compareItem.basePn;
        } else if (
            'uniquePn' in compareItem &&
            typeof compareItem.uniquePn === 'string' &&
            compareItem.uniquePn
        ) {
            return compareItem.uniquePn;
        } else {
            return '';
        }
    };

    // 取得圖片路徑，優先使用 BasePartParameterValue 的 imagePath，再使用 DetectedUniquePart 的 imageUrl
    const getImagePath = (): string => {
        if (!compareItem) return '';
        if (
            'imagePath' in compareItem &&
            typeof compareItem.imagePath === 'string' &&
            compareItem.imagePath
        ) {
            return `https://search.kemet.com/component-images/${compareItem.imagePath}`;
        }
        if (
            'imageUrl' in compareItem &&
            typeof compareItem.imageUrl === 'string' &&
            compareItem.imageUrl
        ) {
            return compareItem.imageUrl;
        }
        return 'https://search.kemet.com/component-images/ceramic_chip_standard.jpg';
    };

    if (compareItem) {
        return (
            <Box
                sx={{
                    position: 'relative', // 為 icon 定位提供相對定位
                    flex: '1 1 auto',
                    p: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    maxWidth: dynamicButtonWidth,
                    backgroundColor: 'white',
                    height: '90px',
                    boxShadow: '0px 4px 4px 0px #00000040',
                    ...sx,
                }}
            >
                {onClearCompareItem && (
                    <IconButton
                        onClick={() => onClearCompareItem(getPartNumber())}
                        sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            width: '24px',
                            height: '24px',
                            padding: 0,
                            backgroundColor: '#B75332',
                            borderRadius: '50%',
                            transition: 'none',
                        }}
                    >
                        <CloseIcon fontSize="small" sx={{ color: 'white' }} />
                    </IconButton>
                )}
                {/* 上方圖片欄位 */}
                <Box
                    component="img"
                    src={getImagePath()}
                    alt={getPartNumber()}
                    sx={{
                        height: '45px',
                        width: '82px',
                        objectFit: 'contain',
                    }}
                />
                {/* 文字放置在最底部 */}
                <Typography
                    sx={{
                        fontFamily: 'Poppins',
                        fontWeight: 600,
                        fontSize: '18px',
                        lineHeight: '117%',
                        letterSpacing: '0%',
                        textAlign: 'center',
                        color: '#B75332',
                        mt: 'auto',
                    }}
                    variant="body1"
                >
                    {getPartNumber()}
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                flex: '1 1 auto',
                p: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                maxWidth: dynamicButtonWidth,
                backgroundColor: 'white',
                height: '90px',
                ...sx,
            }}
        >
            <Typography
                sx={{
                    fontFamily: 'Poppins',
                    fontWeight: 400,
                    fontStyle: 'italic',
                    fontSize: '16px',
                    lineHeight: '117%',
                    letterSpacing: '0%',
                    textAlign: 'center',
                    color: '#CACBCD',
                }}
                variant="body1"
            >
                {contentTree.comparison.addAnotherProductButtonLabel.content}
            </Typography>
        </Box>
    );
};
export default ProductBoxInsideCompareBar;
